import { useState } from 'react'
import { Trans } from 'react-i18next'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { IActionFormProps } from '@makyoapp/makyo-common'
import { ModalWindow16, Screen24, Table16, User16 } from '@makyoapp/makyo-icon/icon'
import {
  Button,
  ButtonVariant,
  Divider,
  Field,
  Label,
  SelectDropdown,
  SingleIconSelect,
  Slider,
} from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import {
  DEFAULT_OVERLAY_PLACEMENT,
  DEFAULT_OVERLAY_SHAPE,
  DEFAULT_OVERLAY_SIZE,
  formatDrawerSize,
  getDirectionLabel,
  getDrawerSize,
  getOverlaySizeLabel,
  overlayPlacements,
  overlayShapeOptions,
  switchDirectionPlacement,
} from '@/shared/overlay'
import { isEmpty } from '@/shared/utils'
import { DeviceTab } from '@/components/DeviceTab'

const formTypeOptions = [
  {
    label: 'This Item',
    value: 'item',
    icon: <Table16 />,
  },
  {
    label: 'User Data',
    value: 'user',
    icon: <User16 />,
  },
]

const screenTypeOptions = [
  {
    label: 'Page',
    value: 'page',
    icon: <Screen24 width={16} height={16} />,
  },
  {
    label: 'Overlay',
    value: 'overlay',
    icon: <ModalWindow16 />,
  },
]

const defaultOverlaySettings = {
  placement: DEFAULT_OVERLAY_PLACEMENT,
  size: DEFAULT_OVERLAY_SIZE,
  shape: DEFAULT_OVERLAY_SHAPE,
  closeBehavior: 'direct',
  opacity: 85,
}

const formatOpacityValue = (val: number) => {
  if (val > 0 && val < 1) {
    return val * 100
  }
  return val
}

export default function ActFormOpenAddItemForm({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const devices = Object.keys(BUILDER_DEVICES) as AppBreakpoint[]

  const [device, setDevice] = useState<AppBreakpoint>('phone')

  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      screenType: initialData?.screenType || 'page',
      overlaySettings: initialData?.overlaySettings || undefined,
    },
    validate(values, setError) {},
  })

  const handleSubmit = onFormSubmit((data) => {
    let actionSettings = data

    if (actionSettings.screenType === 'page') {
      actionSettings.overlaySettings = undefined
    }

    onSubmit(actionSettings)
  })

  const isOverlay = form.getValues('screenType') === 'overlay'

  const value = form.getValues('screenType')
  const invalidForm = isEmpty(value) || !!form.errors

  const handleChangeOverlaySettings = (key: string, value: any) => {
    const currentValue = form.getValues('overlaySettings')

    if (key === 'placement' || key === 'size' || key === 'shape') {
      form.setValues('overlaySettings', {
        ...currentValue,
        [key]: {
          ...currentValue[key],
          [device]: value,
        },
      })
    } else {
      form.setValues('overlaySettings', {
        ...currentValue,
        [key]: value,
      })
    }
  }

  const currentPlacement = form.getValues('overlaySettings')?.placement[device]
  const isCenter = currentPlacement === 'center'
  const currentSize = form.getValues('overlaySettings')?.size[device] || DEFAULT_OVERLAY_SIZE[device]
  const { v1, v2 } = getDrawerSize(currentSize)

  return (
    <div>
      <div className='p-4'>
        <Field>
          <Label required>Screen Type</Label>

          <div className='flex-1 flex items-center gap-2'>
            {screenTypeOptions.map((opt) => (
              <Button
                key={opt.value}
                onClick={() => {
                  form.setValues('screenType', opt.value)

                  if (opt.value === 'page') {
                    form.setValues('overlaySettings', undefined)
                  }
                  if (opt.value === 'overlay' && !form.getValues('overlaySettings')) {
                    form.setValues('overlaySettings', defaultOverlaySettings)
                  }
                }}
                variant={form.getValues('screenType') === opt.value ? ButtonVariant.Normal : ButtonVariant.Gray}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </Field>

        {isOverlay && (
          <>
            <Divider className='border-dashed' />

            {/* <div className='text-light text-m-sm'>Overlay Settings</div>

            <div>Current Device: {device}</div> */}

            <div className='flex overflow-hidden rounded-sm'>
              {devices.map((d) => (
                <DeviceTab key={d} device={d} isActive={device === d} onClick={() => setDevice(d)} />
              ))}
            </div>

            <Field className='mt-4'>
              <Label>Placement</Label>

              <div>
                <SingleIconSelect
                  options={overlayPlacements.map((p) => ({
                    id: p.value,
                    icon: p.icon,
                    title: getDirectionLabel(p.value),
                  }))}
                  selected={switchDirectionPlacement(form.getValues('overlaySettings')?.placement[device])}
                  onClick={(opt) => {
                    handleChangeOverlaySettings('placement', switchDirectionPlacement(opt.id))
                  }}
                />
              </div>
            </Field>

            <Field>
              <Label>{getOverlaySizeLabel(currentPlacement)}</Label>
              <div className='w-[50%]'>
                <Slider
                  values={[v1]}
                  max={100}
                  min={30}
                  step={1}
                  onChange={(val) => {
                    handleChangeOverlaySettings('size', formatDrawerSize('side', currentSize, `${val[0]}%`))
                  }}
                  renderLabel={(val) => `${val}%`}
                />
              </div>
            </Field>

            {isCenter && (
              <Field>
                <Label>Height</Label>
                <div className='w-[50%]'>
                  <Slider
                    values={[v2]}
                    max={100}
                    min={30}
                    step={1}
                    onChange={(val) => {
                      handleChangeOverlaySettings('size', formatDrawerSize('center', currentSize, `${val[0]}%`))
                    }}
                    renderLabel={(val) => `${val}%`}
                  />
                </div>
              </Field>
            )}

            <Field>
              <Label>Corner Radius</Label>
              <div className='w-[250px]'>
                <SelectDropdown
                  value={overlayShapeOptions.find(
                    (opt) => opt.value === form.getValues('overlaySettings')?.shape[device]
                  )}
                  options={overlayShapeOptions}
                  optionLabel={(opt) => opt.label}
                  onChange={(opt) => {
                    handleChangeOverlaySettings('shape', opt.value)
                  }}
                />
              </div>
            </Field>

            <Field>
              <Label>Backdrop Transparancy</Label>
              <div className='w-[50%]'>
                <Slider
                  values={[formatOpacityValue(form.getValues('overlaySettings')?.opacity)]}
                  max={100}
                  min={30}
                  step={1}
                  onChange={(val) => {
                    handleChangeOverlaySettings('opacity', val[0])
                  }}
                  renderLabel={(val) => `${val}%`}
                />
              </div>
            </Field>
          </>
        )}
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
